import { useState, useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import type { LaunchTask, LaunchStatus, LaunchAssignee } from "@/lib/admin-api";
import { LaunchTaskRow } from "./LaunchTaskRow";

interface LaunchPhaseTabProps {
  readonly phaseLabel: string;
  readonly phaseDescription: string;
  readonly tasks: readonly LaunchTask[];
  readonly adminUsers: readonly LaunchAssignee[];
  readonly currentAdminId: string;
  readonly onToggle: (id: string, newStatus: LaunchStatus) => Promise<void>;
  readonly onAssign: (id: string, userId: string | null) => Promise<void>;
  readonly onEdit: (task: LaunchTask) => void;
}

type FilterKey = "all" | "critique" | "best_practice" | "mine" | "todo";

const FILTERS: { key: FilterKey; label: string; dot?: string }[] = [
  { key: "all", label: "Tout" },
  { key: "critique", label: "Critique", dot: "bg-red-500" },
  { key: "best_practice", label: "Best practice", dot: "bg-blue-500" },
  { key: "mine", label: "Assigné à moi" },
  { key: "todo", label: "À faire" },
];

export function LaunchPhaseTab({
  phaseLabel,
  phaseDescription,
  tasks,
  adminUsers,
  currentAdminId,
  onToggle,
  onAssign,
  onEdit,
}: LaunchPhaseTabProps) {
  const [filter, setFilter] = useState<FilterKey>("all");

  const done = useMemo(
    () => tasks.filter((t) => t.status === "done").length,
    [tasks],
  );
  const pct =
    tasks.length > 0 ? Math.round((done / tasks.length) * 100) : 0;

  const filtered = useMemo(() => {
    if (filter === "critique")
      return tasks.filter((t) => t.priority === "critique");
    if (filter === "best_practice")
      return tasks.filter((t) => t.priority === "best_practice");
    if (filter === "mine")
      return tasks.filter((t) => t.assigned_to === currentAdminId);
    if (filter === "todo")
      return tasks.filter((t) => t.status !== "done");
    return tasks;
  }, [tasks, filter, currentAdminId]);

  const criticalLeft = useMemo(
    () =>
      tasks.filter(
        (t) => t.priority === "critique" && t.status !== "done",
      ).length,
    [tasks],
  );

  return (
    <div className="p-6">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="text-sm font-bold text-stone-700">{phaseLabel}</h2>
          <p className="mt-0.5 text-xs text-stone-400">{phaseDescription}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="h-1.5 w-28 overflow-hidden rounded-full bg-stone-100">
            <div
              className="h-full rounded-full bg-[#c2642a] transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-xs font-bold text-[#c2642a]">
            {done}/{tasks.length} · {pct}%
          </span>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
              filter === f.key
                ? "border-stone-800 bg-stone-800 text-white"
                : "border-stone-200 bg-white text-stone-500 hover:border-stone-300"
            }`}
          >
            {f.dot && <span className={`inline-block h-2 w-2 rounded-full ${f.dot} mr-1`} />}
            {f.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg border border-stone-200 bg-white">
        <table className="w-full">
          <thead className="border-b border-stone-200 bg-stone-50">
            <tr>
              <th className="w-10 px-4 py-2" />
              <th className="px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-stone-400">
                Tâche
              </th>
              <th className="w-28 px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-stone-400">
                Priorité
              </th>
              <th className="w-36 px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-stone-400">
                Assigné à
              </th>
              <th className="w-24 px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-stone-400">
                Statut
              </th>
              <th className="w-16 px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-8 text-center text-sm text-stone-400"
                >
                  Aucune tâche pour ce filtre.
                </td>
              </tr>
            ) : (
              filtered.map((task) => (
                <LaunchTaskRow
                  key={task.id}
                  task={task}
                  adminUsers={adminUsers}
                  onToggle={onToggle}
                  onAssign={onAssign}
                  onEdit={onEdit}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {criticalLeft > 0 && (
        <div className="mt-4 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600" />
          <p className="text-xs text-amber-800">
            <strong>
              {criticalLeft} tâche{criticalLeft > 1 ? "s" : ""} critique
              {criticalLeft > 1 ? "s" : ""}
            </strong>{" "}
            non complétée{criticalLeft > 1 ? "s" : ""} dans cette phase.
          </p>
        </div>
      )}
    </div>
  );
}
